// Компонент для проверки доступа к страницам
// Используется для защиты маршрутов на основе permissions

import React from 'react';
import { Navigate } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';

/**
 * Компонент для защиты страниц на основе permissions
 */
export const ProtectedPage = ({
  children,
  requiredPermissions = [],
  fallbackPath = '/unauthorized',
}) => {
  const { isAuthenticated, hasPermission } = useAuth();

  // Если пользователь не аутентифицирован, перенаправляем на логин
  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }
  
  // Если нет требований к permissions, показываем страницу
  if (requiredPermissions.length === 0) {
    return children;
  }
  
  // Проверяем, есть ли у пользователя хотя бы одно из требуемых permissions
  const hasRequiredPermission = requiredPermissions.some(([service, resource, action]) => 
    hasPermission(service, resource, action)
  );
  
  // Если нет нужных permissions, перенаправляем на страницу отказа в доступе
  if (!hasRequiredPermission) {
    return <Navigate to={fallbackPath} replace />;
  }
  
  // Если все проверки пройдены, показываем страницу
  return children;
};

/**
 * Специализированные компоненты для конкретных страниц
 */

// Страница талантов - требует permission на просмотр талантов
export const TalentsPageGuard = ({ children }) => {
  return (
    <ProtectedPage requiredPermissions={[['talent-service', 'talent', 'list']]}>
      {children}
    </ProtectedPage>
  );
};

// Страница кандидатов - требует permission на просмотр кандидатов
export const CandidatesPageGuard = ({ children }) => {
  return (
    <ProtectedPage requiredPermissions={[['talent-service', 'candidate', 'list']]}>
      {children}
    </ProtectedPage>
  );
};

// Страница сотрудников - требует permission на просмотр сотрудников
export const EmployeesPageGuard = ({ children }) => {
  return (
    <ProtectedPage requiredPermissions={[['talent-service', 'employee', 'list']]}>
      {children}
    </ProtectedPage>
  );
};

// Страница всех сотрудников (включая уволенных) - требует специальное permission list-all
export const EmployeesAllPageGuard = ({ children }) => {
  return (
    <ProtectedPage requiredPermissions={[['talent-service', 'employee', 'list-all']]}>
      {children}
    </ProtectedPage>
  );
};

// Страница предпродаж - требует permission на просмотр предпродаж
export const PreSalesPageGuard = ({ children }) => {
  return (
    <ProtectedPage requiredPermissions={[['talent-service', 'presale', 'list']]}>
      {children}
    </ProtectedPage>
  );
};

// Страница стажёров - требует permission на просмотр стажёров
export const TraineesPageGuard = ({ children }) => {
  return (
    <ProtectedPage requiredPermissions={[['talent-service', 'trainee', 'list']]}>
      {children}
    </ProtectedPage>
  );
};

// Страница управления статусами - требует permission на просмотр статусов
export const StatusManagementPageGuard = ({ children }) => {
  const { hasPermission } = useAuth();
  const canReadStatus = hasPermission('talent-service', 'status', 'read');
  const canListStatus = hasPermission('talent-service', 'status', 'list');
  const permissions = [];
  if (canReadStatus) permissions.push(['talent-service', 'status', 'read']);
  if (canListStatus) permissions.push(['talent-service', 'status', 'list']);
  return (
    <ProtectedPage requiredPermissions={permissions}>
      {children}
    </ProtectedPage>
  );
};

// Страница управления стадиями — собственный ресурс прав stage.*, не status.*:
// стадия отдельная ось от статуса, права на одно не должны означать права на другое
export const StageManagementPageGuard = ({ children }) => {
  const { hasPermission } = useAuth();
  const canReadStage = hasPermission('talent-service', 'stage', 'read');
  const canListStage = hasPermission('talent-service', 'stage', 'list');
  const permissions = [];
  if (canReadStage) permissions.push(['talent-service', 'stage', 'read']);
  if (canListStage) permissions.push(['talent-service', 'stage', 'list']);
  return (
    <ProtectedPage requiredPermissions={permissions}>
      {children}
    </ProtectedPage>
  );
};

// Страница создания таланта - требует permission на создание талантов
export const TalentFormPageGuard = ({ children }) => {
  return (
    <ProtectedPage requiredPermissions={[['talent-service', 'talent', 'create']]}>
      {children}
    </ProtectedPage>
  );
};

// Страница редактирования таланта - требует permission на редактирование
export const TalentEditPageGuard = ({ children }) => {
  const { hasPermission } = useAuth();
  const canUpdateCandidate = hasPermission('talent-service', 'candidate', 'update');
  const canUpdateTrainee   = hasPermission('talent-service', 'trainee',   'update');
  const canUpdateEmployee  = hasPermission('talent-service', 'employee',  'update');
  const canUpdatePresale   = hasPermission('talent-service', 'presale',   'update');
  const permissions = [];
  if (canUpdateCandidate) permissions.push(['talent-service', 'candidate', 'update']);
  if (canUpdateTrainee)   permissions.push(['talent-service', 'trainee',   'update']);
  if (canUpdateEmployee)  permissions.push(['talent-service', 'employee',  'update']);
  if (canUpdatePresale)   permissions.push(['talent-service', 'presale',   'update']);
  return (
    <ProtectedPage requiredPermissions={permissions}>
      {children}
    </ProtectedPage>
  );
};

// Страница детального просмотра таланта - требует permission на чтение
export const TalentDetailPageGuard = ({ children }) => {
  const { hasPermission } = useAuth();
  const canReadCandidate = hasPermission('talent-service', 'candidate', 'read');
  const canReadTrainee   = hasPermission('talent-service', 'trainee',   'read');
  const canReadEmployee  = hasPermission('talent-service', 'employee',  'read');
  const canReadPresale   = hasPermission('talent-service', 'presale',   'read');
  const permissions = [];
  if (canReadCandidate) permissions.push(['talent-service', 'candidate', 'read']);
  if (canReadTrainee)   permissions.push(['talent-service', 'trainee',   'read']);
  if (canReadEmployee)  permissions.push(['talent-service', 'employee',  'read']);
  if (canReadPresale)   permissions.push(['talent-service', 'presale',   'read']);
  return (
    <ProtectedPage requiredPermissions={permissions}>
      {children}
    </ProtectedPage>
  );
};

// Страница календаря - требует доступ хотя бы к одному типу талантов
export const CalendarPageGuard = ({ children }) => {
  const { hasPermission } = useAuth();
  const canListCandidate = hasPermission('talent-service', 'candidate', 'list');
  const canListTrainee   = hasPermission('talent-service', 'trainee',   'list');
  const canListEmployee  = hasPermission('talent-service', 'employee',  'list');
  const canListPresale   = hasPermission('talent-service', 'presale',   'list');
  const permissions = [];
  if (canListCandidate) permissions.push(['talent-service', 'candidate', 'list']);
  if (canListTrainee)   permissions.push(['talent-service', 'trainee',   'list']);
  if (canListEmployee)  permissions.push(['talent-service', 'employee',  'list']);
  if (canListPresale)   permissions.push(['talent-service', 'presale',   'list']);
  return (
    <ProtectedPage requiredPermissions={permissions}>
      {children}
    </ProtectedPage>
  );
};

// Страница управления резервными копиями - требует permission на чтение бэкапов
export const BackupManagementPageGuard = ({ children }) => {
  const { hasPermission } = useAuth();

  // Требуется право на чтение бэкапов
  if (!hasPermission('talent-service', 'backup', 'read')) {
    return <Navigate to="/unauthorized" replace />;
  }

  return children;
};

// Страница Activity Feed - требует permission на просмотр событий
export const ActivityFeedPageGuard = ({ children }) => {
  return (
    <ProtectedPage requiredPermissions={[['talent-service', 'events', 'list']]}>
      {children}
    </ProtectedPage>
  );
};

export default ProtectedPage;
